import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

export default function Profile() {
    const { user, type, loading, refresh } = useAuth();
    const [malls, setMalls] = useState([]);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.get('/malls').then(({ data }) => setMalls(data)); }, []);
    useEffect(() => { if (user) setForm(user); }, [user]);

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
    if (type !== 'customer') return <Navigate to="/login?next=/profile" replace />;

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                name: form.name, mobile: form.mobile, gender: form.gender, dob: form.dob,
                anniversary: form.anniversary, city: form.city, preferred_mall_id: form.preferred_mall_id,
                whatsapp_optin: form.whatsapp_optin, email_optin: form.email_optin,
                sms_optin: form.sms_optin, push_optin: form.push_optin,
            };
            await api.put('/customers/me', payload);
            toast.success('Profile updated');
            await refresh();
        } catch (e) {
            toast.error('Could not save');
        } finally { setSaving(false); }
    };

    return (
        <div className="pt-16 pb-32" data-testid="profile-page">
            <section className="max-w-5xl mx-auto px-6 md:px-12 pt-12">
                <div className="overline mb-4">Profile</div>
                <h1 className="font-serif text-5xl md:text-6xl text-navy leading-tight">Hello, <span className="italic text-gold">{user.name}.</span></h1>
                <div className="mt-4 text-text-secondary">Member since {new Date(user.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })} • {user.membership_id}</div>
            </section>

            <section className="max-w-5xl mx-auto px-6 md:px-12 mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="profile-quicklinks">
                {[
                    ['Orders', '/orders'],
                    ['Address book', '/account/addresses'],
                    ['Returns', '/account/returns'],
                    ['Mall Pass', '/mall-pass'],
                ].map(([label, to]) => (
                    <Link key={to} to={to} className="card-luxury p-4 hover:border-inorbit-orange transition-colors flex items-center justify-between" data-testid={`quick-${to.replace(/\//g, '-')}`}>
                        <span className="text-sm font-serif text-inorbit-blue">{label}</span>
                        <span className="text-inorbit-orange">→</span>
                    </Link>
                ))}
            </section>

            <section className="max-w-5xl mx-auto px-6 md:px-12 mt-12 card-luxury p-10 grid sm:grid-cols-2 gap-x-10 gap-y-8" data-testid="profile-form">
                <Field label="Name" value={form.name || ''} onChange={set('name')} testid="profile-name" />
                <Field label="Mobile" value={form.mobile || ''} onChange={set('mobile')} testid="profile-mobile" />
                <Field label="Email" value={form.email || ''} disabled testid="profile-email" />
                <Field label="City" value={form.city || ''} onChange={set('city')} testid="profile-city" />
                <Field label="Gender" value={form.gender || ''} onChange={set('gender')} testid="profile-gender" type="select" options={['', 'female', 'male', 'other', 'prefer_not_to_say']} />
                <Field label="Date of Birth" value={form.dob || ''} onChange={set('dob')} testid="profile-dob" type="date" />
                <Field label="Anniversary" value={form.anniversary || ''} onChange={set('anniversary')} testid="profile-anniv" type="date" />
                <Field label="Preferred Mall" value={form.preferred_mall_id || ''} onChange={set('preferred_mall_id')} testid="profile-pref-mall" type="select" options={[['', 'Select'], ...malls.map((m) => [m.id, m.name])]} />

                <div className="sm:col-span-2 border-t border-border-subtle pt-6">
                    <div className="overline mb-4">Communications</div>
                    <div className="grid sm:grid-cols-4 gap-4">
                        {[
                            ['whatsapp_optin', 'WhatsApp'],
                            ['email_optin', 'Email'],
                            ['sms_optin', 'SMS'],
                            ['push_optin', 'Push'],
                        ].map(([k, lbl]) => (
                            <label key={k} className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!form[k]} onChange={setBool(k)} data-testid={`profile-${k}`} />
                                {lbl}
                            </label>
                        ))}
                    </div>
                </div>
                <div className="sm:col-span-2 mt-4">
                    <button onClick={save} disabled={saving} className="btn-primary" data-testid="profile-save-btn">
                        {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>
            </section>
        </div>
    );
}

function Field({ label, value, onChange, disabled, testid, type = 'text', options }) {
    return (
        <div>
            <label className="overline">{label}</label>
            {type === 'select' ? (
                <select className="input-luxury" value={value || ''} onChange={onChange} disabled={disabled} data-testid={testid}>
                    {(options || []).map((o) => {
                        const [v, l] = Array.isArray(o) ? o : [o, o];
                        return <option key={v} value={v}>{l || v || '—'}</option>;
                    })}
                </select>
            ) : (
                <input type={type} className="input-luxury" value={value} onChange={onChange} disabled={disabled} data-testid={testid} />
            )}
        </div>
    );
}
